import { BoxCollider, PlaneCollider } from './physics/index.js';
import {
  HERO_BUILDING_CONFIG,
  floorDefinitions,
  getHeroBuildingInstances,
  getHeroBuildingColliders,
  getHeroFloorAt,
  isInsideHeroBuilding
} from './heroBuilding.js';
import { getVehicleCollider } from './vehicles.js';

/**
 * City Layout bounds and metadata for Fortnite/GTA-style Downtown Town.
 */
export const CITY_BOUNDS = {
  min: { x: -45, y: 0, z: -45 },
  max: { x: 45, y: 45, z: 45 },
  width: 90,
  depth: 90
};

export const DISTRICT_INFO = {
  name: 'Tilted Towers Downtown Plaza',
  dimensions: '90m x 90m',
  architecture: 'Fortnite & GTA-style High-Fidelity 3D Downtown',
  features: '4-Story Walkable Hero Building, Clock Tower, Storefronts, Paved Roads, Parked Vehicles, Sidewalks, Streetlamps'
};

function createManifestItem({
  id,
  name,
  type,
  modelPath,
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = [1, 1, 1],
  colliderSpec = null,
  castShadow = true,
  receiveShadow = true,
  vehicleType = null
}) {
  return {
    id,
    name: name || id,
    type: type || 'prop',
    modelPath,
    position: [...position],
    rotation: [...rotation],
    scale: [...scale],
    colliderSpec,
    castShadow,
    receiveShadow,
    vehicleType
  };
}

/**
 * Generate Main Street, Crossroad, and Sidewalks network.
 */
function generateRoadNetwork() {
  const roadObjects = [];
  const roadColliders = [];
  let roadId = 1;

  // Road tiles are scaled by 4.0 so each tile is 4.0m x 4.0m wide (true 2-lane roadway)
  const roadTileScale = [4, 4, 4];
  const HALF_PI = Math.PI * 0.5;

  function addRoad(name, model, x, z, ry = 0) {
    roadObjects.push(createManifestItem({
      id: `road_${roadId++}_${name}`,
      name,
      type: 'road',
      modelPath: `/assets/roads/${model}`,
      position: [x, 0.02, z],
      rotation: [0, ry, 0],
      scale: roadTileScale,
      castShadow: false,
      receiveShadow: true
    }));
  }

  // 1. Central 4-way Intersection at (0, 0)
  addRoad('crossroad_center', 'road-crossroad.glb', 0, 0, 0);

  // 2. North-South Main Avenue (Z: -28 to +28 at X=0)
  for (let z = -28; z <= 28; z += 4) {
    if (z === 0) continue;
    if (z === 4 || z === -4) {
      addRoad(`crossing_ns_${z}`, 'road-crossing.glb', 0, z, 0);
    } else {
      addRoad(`straight_ns_${z}`, 'road-straight.glb', 0, z, 0);
    }
  }

  // 3. East-West Commercial Boulevard (X: -28 to +28 at Z=0)
  for (let x = -28; x <= 28; x += 4) {
    if (x === 0) continue;
    if (x === 4 || x === -4) {
      addRoad(`crossing_ew_${x}`, 'road-crossing.glb', x, 0, HALF_PI);
    } else {
      addRoad(`straight_ew_${x}`, 'road-straight.glb', x, 0, HALF_PI);
    }
  }

  // Road Surface Physical Colliders (Smooth driving/walking plane)
  roadColliders.push(new BoxCollider({
    name: 'main_avenue_road_collider',
    center: { x: 0, y: 0.05, z: 0 },
    size: { x: 8.0, y: 0.1, z: 64.0 },
    isWalkable: true
  }));
  roadColliders.push(new BoxCollider({
    name: 'cross_boulevard_road_collider',
    center: { x: 0, y: 0.05, z: 0 },
    size: { x: 64.0, y: 0.1, z: 8.0 },
    isWalkable: true
  }));

  // Concrete Sidewalk Curbs along Main Avenue & Boulevard (Height = 0.25m, Width = 3.5m)
  // NW Sidewalk Slab
  roadColliders.push(new BoxCollider({
    name: 'sidewalk_nw',
    center: { x: -16.0, y: 0.15, z: -16.0 },
    size: { x: 24.0, y: 0.3, z: 24.0 },
    isWalkable: true
  }));
  // NE Sidewalk Slab
  roadColliders.push(new BoxCollider({
    name: 'sidewalk_ne',
    center: { x: 16.0, y: 0.15, z: -16.0 },
    size: { x: 24.0, y: 0.3, z: 24.0 },
    isWalkable: true
  }));
  // SW Sidewalk Slab
  roadColliders.push(new BoxCollider({
    name: 'sidewalk_sw',
    center: { x: -16.0, y: 0.15, z: 16.0 },
    size: { x: 24.0, y: 0.3, z: 24.0 },
    isWalkable: true
  }));
  // SE Sidewalk Slab
  roadColliders.push(new BoxCollider({
    name: 'sidewalk_se',
    center: { x: 16.0, y: 0.15, z: 16.0 },
    size: { x: 24.0, y: 0.3, z: 24.0 },
    isWalkable: true
  }));

  return { roadObjects, roadColliders };
}

/**
 * Generate 5 iconic, human-scale commercial buildings & high-rises.
 */
function generateCommercialDistrict() {
  const commercialObjects = [];
  const commercialColliders = [];
  const HALF_PI = Math.PI * 0.5;
  const PI = Math.PI;

  // 1. Clock Tower / Spire Tower (Fortnite style clock tower, North-East plaza at X=16, Z=-16)
  // Height: ~26m tall, 8m wide
  commercialObjects.push(createManifestItem({
    id: 'building_clock_tower',
    name: 'Downtown Town Clock Tower',
    type: 'building',
    modelPath: '/assets/commercial/building-skyscraper-c.glb',
    position: [16.0, 0, -16.0],
    rotation: [0, 0, 0],
    scale: [7.0, 9.0, 7.0]
  }));
  commercialColliders.push(new BoxCollider({
    name: 'clock_tower_collider',
    center: { x: 16.0, y: 13.0, z: -16.0 },
    size: { x: 8.5, y: 26.0, z: 8.5 },
    isWalkable: false
  }));

  // 2. Pawn Shop & Retail Store (South-West corner at X=-16, Z=14 - matching reference photo foreground!)
  commercialObjects.push(createManifestItem({
    id: 'building_pawn_shop',
    name: 'Gold Pawn & Retail Store',
    type: 'building',
    modelPath: '/assets/commercial/building-h.glb',
    position: [-16.0, 0, 14.0],
    rotation: [0, 0, 0],
    scale: [7.5, 6.0, 7.5]
  }));
  // Storefront blue awning
  commercialObjects.push(createManifestItem({
    id: 'pawn_shop_awning',
    name: 'Pawn Shop Entrance Awning',
    type: 'prop',
    modelPath: '/assets/commercial/detail-awning-wide.glb',
    position: [-16.0, 3.5, 10.0],
    rotation: [0, 0, 0],
    scale: [4.0, 4.0, 4.0]
  }));
  commercialColliders.push(new BoxCollider({
    name: 'pawn_shop_collider',
    center: { x: -16.0, y: 6.0, z: 14.0 },
    size: { x: 8.0, y: 12.0, z: 8.0 },
    isWalkable: false
  }));

  // 3. Grand Department Store (South-East corner at X=16, Z=14)
  commercialObjects.push(createManifestItem({
    id: 'building_department_store',
    name: 'Grand Commercial Department Store',
    type: 'building',
    modelPath: '/assets/commercial/building-a.glb',
    position: [16.0, 0, 14.0],
    rotation: [0, 0, 0],
    scale: [7.5, 7.5, 7.5]
  }));
  commercialObjects.push(createManifestItem({
    id: 'department_store_awning',
    name: 'Department Store Red Awning',
    type: 'prop',
    modelPath: '/assets/commercial/detail-awning-wide.glb',
    position: [16.0, 3.8, 10.0],
    rotation: [0, 0, 0],
    scale: [4.0, 4.0, 4.0]
  }));
  commercialColliders.push(new BoxCollider({
    name: 'department_store_collider',
    center: { x: 16.0, y: 6.5, z: 14.0 },
    size: { x: 8.0, y: 13.0, z: 8.0 },
    isWalkable: false
  }));

  // 4. Metropolitan Bank & High-Rise (North-West corner at X=-16, Z=-16)
  commercialObjects.push(createManifestItem({
    id: 'building_metro_bank',
    name: 'Metropolitan Bank & High-Rise Tower',
    type: 'building',
    modelPath: '/assets/commercial/building-skyscraper-a.glb',
    position: [-16.0, 0, -16.0],
    rotation: [0, HALF_PI, 0],
    scale: [7.0, 8.5, 7.0]
  }));
  commercialColliders.push(new BoxCollider({
    name: 'metro_bank_collider',
    center: { x: -16.0, y: 12.0, z: -16.0 },
    size: { x: 8.5, y: 24.0, z: 8.5 },
    isWalkable: false
  }));

  // 5. City Plaza Cafe & Bistro (Outdoor Patio Dining at X=-18, Z=0)
  commercialObjects.push(createManifestItem({
    id: 'building_plaza_bistro',
    name: 'City Plaza Bistro & Bakery',
    type: 'building',
    modelPath: '/assets/commercial/building-c.glb',
    position: [-24.0, 0, 0.0],
    rotation: [0, HALF_PI, 0],
    scale: [7.0, 6.5, 7.0]
  }));
  // Outdoor Dining Parasols
  commercialObjects.push(createManifestItem({
    id: 'bistro_parasol_1',
    name: 'Bistro Dining Parasol A',
    type: 'prop',
    modelPath: '/assets/commercial/detail-parasol-a.glb',
    position: [-14.5, 0.25, 2.5],
    rotation: [0, 0.3, 0],
    scale: [3.0, 3.0, 3.0]
  }));
  commercialObjects.push(createManifestItem({
    id: 'bistro_parasol_2',
    name: 'Bistro Dining Parasol B',
    type: 'prop',
    modelPath: '/assets/commercial/detail-parasol-b.glb',
    position: [-14.5, 0.25, -2.5],
    rotation: [0, -0.4, 0],
    scale: [3.0, 3.0, 3.0]
  }));
  commercialColliders.push(new BoxCollider({
    name: 'plaza_bistro_collider',
    center: { x: -24.0, y: 6.0, z: 0.0 },
    size: { x: 8.0, y: 12.0, z: 8.0 },
    isWalkable: false
  }));

  return { commercialObjects, commercialColliders };
}

/**
 * Generate 3D Parked Vehicles (Green Pickup Truck, Yellow Sedan, Work Van).
 */
function generateVehicles() {
  const vehicleObjects = [];
  const vehicleColliders = [];
  const HALF_PI = Math.PI * 0.5;

  // 1. Vintage Green Pickup Truck (Curbside right foreground at X=4.8, Z=8.0 - matching reference photo!)
  vehicleObjects.push(createManifestItem({
    id: 'vehicle_green_pickup',
    name: 'Vintage Green Pickup Truck',
    type: 'vehicle',
    vehicleType: 'pickup',
    modelPath: null, // Generated via createVehicleMesh('pickup')
    position: [4.8, 0.1, 8.0],
    rotation: [0, 0, 0],
    scale: [1, 1, 1]
  }));
  vehicleColliders.push(getVehicleCollider('pickup', [4.8, 0.1, 8.0], 0));

  // 2. Yellow Classic Sedan (Parked across intersection at X=-4.8, Z=-8.0)
  vehicleObjects.push(createManifestItem({
    id: 'vehicle_yellow_sedan',
    name: 'Yellow Classic Sedan',
    type: 'vehicle',
    vehicleType: 'sedan',
    modelPath: null,
    position: [-4.8, 0.1, -8.0],
    rotation: [0, Math.PI, 0],
    scale: [1, 1, 1]
  }));
  vehicleColliders.push(getVehicleCollider('sedan', [-4.8, 0.1, -8.0], Math.PI));

  // 3. Work Delivery Van (Parked near pawn shop alley at X=-12.0, Z=4.8)
  vehicleObjects.push(createManifestItem({
    id: 'vehicle_delivery_van',
    name: 'Commercial Delivery Van',
    type: 'vehicle',
    vehicleType: 'delivery',
    modelPath: null,
    position: [-12.0, 0.1, 4.8],
    rotation: [0, HALF_PI, 0],
    scale: [1, 1, 1]
  }));
  vehicleColliders.push(getVehicleCollider('delivery', [-12.0, 0.1, 4.8], HALF_PI));

  return { vehicleObjects, vehicleColliders };
}

/**
 * Generate streetlights, traffic signals, payphones, fire hydrants, dumpsters.
 */
function generateStreetDressing() {
  const dressingObjects = [];
  const dressingColliders = [];
  const dressingLights = [];
  const HALF_PI = Math.PI * 0.5;
  const PI = Math.PI;

  const lampScale = [2.8, 2.8, 2.8];
  const trafficLightScale = [2.8, 2.8, 2.8];

  // 1. Street Lamps (Placed at sidewalk curb corners)
  const lampPositions = [
    { x: -5.0, z: -5.0, ry: 0 },
    { x: 5.0, z: -5.0, ry: -HALF_PI },
    { x: -5.0, z: 5.0, ry: HALF_PI },
    { x: 5.0, z: 5.0, ry: PI },
    { x: -5.0, z: -20.0, ry: 0 },
    { x: 5.0, z: -20.0, ry: PI },
    { x: -5.0, z: 20.0, ry: 0 },
    { x: 5.0, z: 20.0, ry: PI }
  ];

  lampPositions.forEach((pos, idx) => {
    dressingObjects.push(createManifestItem({
      id: `street_lamp_${idx + 1}`,
      name: `Street Lamp (${pos.x}, ${pos.z})`,
      type: 'street_dressing',
      modelPath: '/assets/roads/light-curved.glb',
      position: [pos.x, 0.15, pos.z],
      rotation: [0, pos.ry, 0],
      scale: lampScale
    }));
    dressingLights.push({
      id: `street_light_pt_${idx + 1}`,
      position: [pos.x, 6.2, pos.z],
      color: 0xffe8aa,
      intensity: 1.8,
      distance: 18,
      castShadow: true
    });
  });

  // 2. Traffic Lights at 4-way intersection
  const trafficPositions = [
    { x: -4.5, z: -4.5, ry: 0 },
    { x: 4.5, z: 4.5, ry: PI }
  ];
  trafficPositions.forEach((pos, idx) => {
    dressingObjects.push(createManifestItem({
      id: `traffic_light_${idx + 1}`,
      name: `Intersection Traffic Light ${idx + 1}`,
      type: 'street_dressing',
      modelPath: '/assets/roads/traffic-light.glb',
      position: [pos.x, 0.15, pos.z],
      rotation: [0, pos.ry, 0],
      scale: trafficLightScale
    }));
  });

  // 3. Stop Signs & Road Signs
  dressingObjects.push(createManifestItem({
    id: 'sign_stop_1',
    name: 'Crosswalk Stop Sign',
    type: 'street_dressing',
    modelPath: '/assets/roads/road-sign-stop.glb',
    position: [-4.8, 0.15, 6.5],
    rotation: [0, 0, 0],
    scale: [2.5, 2.5, 2.5]
  }));

  // 4. Dumpsters in service back-alleys
  dressingObjects.push(createManifestItem({
    id: 'dumpster_green_1',
    name: 'Commercial Dumpster',
    type: 'street_dressing',
    modelPath: '/assets/roads/dumpster.glb',
    position: [-10.5, 0.15, 12.0],
    rotation: [0, HALF_PI, 0],
    scale: [2.2, 2.2, 2.2]
  }));
  dressingColliders.push(new BoxCollider({
    name: 'dumpster_collider',
    center: { x: -10.5, y: 0.9, z: 12.0 },
    size: { x: 2.5, y: 1.5, z: 1.6 },
    isWalkable: false
  }));

  // 5. Fire Hydrants
  dressingObjects.push(createManifestItem({
    id: 'hydrant_red_1',
    name: 'Curbside Fire Hydrant',
    type: 'street_dressing',
    modelPath: '/assets/roads/hydrant.glb',
    position: [5.2, 0.15, 4.0],
    rotation: [0, 0, 0],
    scale: [2.5, 2.5, 2.5]
  }));

  return { dressingObjects, dressingColliders, dressingLights };
}

/**
 * Generate tall pine trees, green oaks, hedges, flowerbeds, and park landscaping.
 */
function generateNatureLandscaping() {
  const natureObjects = [];
  const natureColliders = [];

  const treeScaleLarge = [5.5, 6.5, 5.5];
  const treeScaleMedium = [4.5, 5.0, 4.5];

  // Tall Trees along Sidewalks & Town Plaza (Height ~8m to 12m tall!)
  const trees = [
    { model: 'tree_oak.glb', x: -10.0, z: -10.0, scale: treeScaleLarge },
    { model: 'tree_pineDefaultA.glb', x: -10.0, z: -22.0, scale: treeScaleLarge },
    { model: 'tree_detailed.glb', x: 10.0, z: -10.0, scale: treeScaleLarge },
    { model: 'tree_pineDefaultA.glb', x: 10.0, z: -22.0, scale: treeScaleLarge },
    { model: 'tree_oak.glb', x: -10.0, z: 22.0, scale: treeScaleMedium },
    { model: 'tree_detailed.glb', x: 10.0, z: 22.0, scale: treeScaleLarge },
    { model: 'tree_pineDefaultA.glb', x: 24.0, z: 0.0, scale: treeScaleLarge },
    { model: 'tree_oak.glb', x: 24.0, z: -10.0, scale: treeScaleMedium }
  ];

  trees.forEach((t, idx) => {
    natureObjects.push(createManifestItem({
      id: `tree_${idx + 1}`,
      name: `Canopy Tree (${t.x}, ${t.z})`,
      type: 'nature',
      modelPath: `/assets/nature/${t.model}`,
      position: [t.x, 0.15, t.z],
      rotation: [0, (idx * 0.8), 0],
      scale: t.scale
    }));
    // Solid tree trunk collision
    natureColliders.push(new BoxCollider({
      name: `tree_trunk_${idx + 1}`,
      center: { x: t.x, y: 3.0, z: t.z },
      size: { x: 1.2, y: 6.0, z: 1.2 },
      isWalkable: false
    }));
  });

  // Rocks & Boulders in the town square
  natureObjects.push(createManifestItem({
    id: 'rock_plaza_1',
    name: 'Plaza Landscape Boulder',
    type: 'nature',
    modelPath: '/assets/nature/stone_largeA.glb',
    position: [12.0, 0.15, -4.0],
    rotation: [0, 0.4, 0],
    scale: [3.0, 2.5, 3.0]
  }));
  natureColliders.push(new BoxCollider({
    name: 'rock_plaza_collider',
    center: { x: 12.0, y: 0.8, z: -4.0 },
    size: { x: 2.0, y: 1.4, z: 2.0 },
    isWalkable: false
  }));

  return { natureObjects, natureColliders };
}

/**
 * Generate continuous lush green grass ground terrain.
 */
function generateGroundTerrain() {
  const groundColliders = [
    new PlaneCollider({
      name: 'ground_plane_grass',
      point: { x: 0, y: 0, z: 0 },
      normal: { x: 0, y: 1, z: 0 },
      size: [-45, 45, -45, 45],
      isWalkable: true,
      friction: 0.8
    })
  ];

  return { groundColliders };
}

/**
 * Main function returning the complete City Scene Manifest.
 */
export function getCitySceneManifest() {
  const { groundColliders } = generateGroundTerrain();
  const { roadObjects, roadColliders } = generateRoadNetwork();
  const { commercialObjects, commercialColliders } = generateCommercialDistrict();
  const { vehicleObjects, vehicleColliders } = generateVehicles();
  const { dressingObjects, dressingColliders, dressingLights } = generateStreetDressing();
  const { natureObjects, natureColliders } = generateNatureLandscaping();

  // 4-Story Hero Building at North lot (Z = -6.0)
  const heroInstances = getHeroBuildingInstances();
  const heroColliders = getHeroBuildingColliders();

  const heroObjects = heroInstances.map((m, idx) => ({
    id: m.id || `hero_part_${idx + 1}`,
    name: `Hero Building ${m.name || m.type}`,
    type: 'hero_building',
    modelPath: m.modelUrl,
    position: m.position,
    rotation: m.rotation,
    scale: m.scale,
    castShadow: true,
    receiveShadow: true
  }));

  const allObjects = [
    ...roadObjects,
    ...heroObjects,
    ...commercialObjects,
    ...vehicleObjects,
    ...dressingObjects,
    ...natureObjects
  ];

  const allColliders = [
    ...groundColliders,
    ...roadColliders,
    ...heroColliders,
    ...commercialColliders,
    ...vehicleColliders,
    ...dressingColliders,
    ...natureColliders
  ];

  const allLights = [
    ...dressingLights
  ];

  allObjects.objects = allObjects;
  allObjects.models = allObjects;
  allObjects.colliders = allColliders;
  allObjects.lights = allLights;
  allObjects.bounds = CITY_BOUNDS;
  allObjects.info = DISTRICT_INFO;
  allObjects.heroConfig = HERO_BUILDING_CONFIG;
  allObjects.heroFloors = floorDefinitions;
  allObjects.getHeroFloorAt = getHeroFloorAt;
  allObjects.isInsideHeroBuilding = isInsideHeroBuilding;

  return allObjects;
}

export default getCitySceneManifest;
